import { FACE_EMBEDDING_DIMENSIONS, FACE_EMBEDDING_MODEL } from '@familienbaum/shared';
import type { InferenceSession, Tensor } from 'onnxruntime-web';

/**
 * Aus einem Gesicht eine Zahlenreihe machen, die sich mit anderen Gesichtern
 * vergleichen lässt – der Schritt von „hier ist ein Gesicht" zu „das ist
 * dieselbe Person".
 *
 * Das Modell ist **SFace** aus dem OpenCV Zoo (Apache 2.0), eine
 * MobileFaceNet-Variante. Bewusst nicht ArcFace/InsightFace: deren Modelle sind
 * ausdrücklich nur für nicht-kommerzielle Forschung freigegeben.
 *
 * Läuft im Browser über `onnxruntime-web`, aus demselben Grund wie die
 * Erkennung: das Bild verlässt das Gerät nicht, und der API-Container bleibt
 * klein – `onnxruntime-node` wären 296 MB im Image, auf einem kleinen Server
 * ein spürbarer Brocken. Zum Server geht am Ende nur die Zahlenreihe.
 *
 * ## Ausrichten ist der halbe Erfolg
 *
 * Ein Merkmalsmodell erwartet das Gesicht in einer festen Lage: Augen
 * waagerecht, immer an derselben Stelle im 112 × 112 großen Eingabebild. Wird
 * stattdessen einfach der Markierungsrahmen hineinskaliert, unterscheiden sich
 * zwei Aufnahmen derselben Person schon durch einen geneigten Kopf mehr als
 * zwei verschiedene Personen in gleicher Haltung.
 *
 * Die Punkte dafür liefert BlazeFace mit – Augen, Nasenspitze und Mundmitte.
 * Aus drei Punktpaaren wird eine Ähnlichkeitstransformation (Drehung,
 * Maßstab, Verschiebung) gerechnet und das Gesicht damit in die Normlage
 * gedreht. Genau deshalb ist der Browser hier nicht bloß der leichtere, sondern
 * der bessere Ort: serverseitig lägen diese Punkte nicht vor.
 */

/**
 * Kennung des Modells, die zu jeder Zahlenreihe mitgeschrieben wird.
 *
 * Unverzichtbar: Vektoren aus verschiedenen Modellen sind nicht vergleichbar.
 * Ohne diese Spalte wäre der erste Modellwechsel ein Datenfriedhof, in dem
 * niemand mehr unterscheiden kann, was noch gilt.
 */
export const EMBEDDING_MODEL = FACE_EMBEDDING_MODEL;

/** Länge der Zahlenreihe, die SFace ausgibt. */
export const EMBEDDING_DIMENSIONS = FACE_EMBEDDING_DIMENSIONS;

/**
 * Die fp32-Fassung, nicht die int8-quantisierte.
 *
 * Gemessen an 37 benannten Gesichtern dieses Archivs (Leave-one-out): fp32
 * findet 28 von 32 richtig, int8 27 – und wichtiger, der stärkste *falsche*
 * Treffer sinkt von 0,594 auf 0,547. Mit dem Abstandskriterium (siehe API)
 * ergibt das 14 fehlerfreie Vorschläge statt 13 richtiger und einem falschen.
 *
 * Der Preis sind 38,7 MB statt 9,9 – geholt erst beim ersten Nachrechnen und
 * danach im Cache des Browsers.
 */
const MODEL_URL = '/models/face_recognition_sface_2021dec.onnx';

/**
 * Die WASM-Laufzeit liegt selbst ausgeliefert daneben – kein CDN.
 *
 * Zwei Feinheiten, die beide einen halben Nachmittag gekostet hätten:
 *
 * Geladen wird ausdrücklich `onnxruntime-web/wasm` und nicht das Hauptbündel.
 * Das zöge die WebGPU-Fassung nach (`…jsep.wasm`, 28 MB statt 14) und läuft ins
 * Leere, wenn die nicht mitgeliefert wird.
 *
 * Und überschrieben wird nur der Pfad der `.wasm`, nicht das ganze Verzeichnis.
 * Ein Verzeichnis lässt die Laufzeit auch ihren JavaScript-Lader von dort holen
 * – per dynamischem `import()`, an das Vite ein `?import` anhängt, worauf der
 * Entwicklungsserver mit 500 antwortet. Das Bündel hat den Lader längst
 * eingebaut; benannt wird deshalb genau eine Datei.
 */
const WASM_FILE = '/onnxruntime/ort-wasm-simd-threaded.wasm';

/** Eingabekantenlänge des Modells. */
const INPUT_PX = 112;

/**
 * Normlage der Gesichtspunkte im 112 × 112 großen Eingabebild.
 *
 * Die Zahlen sind die eingeführte ArcFace-Schablone, auf die auch SFace
 * trainiert ist. Der Mundpunkt ist die Mitte der beiden Mundwinkel der
 * Schablone (41,55/92,37 und 70,73/92,20) – BlazeFace liefert nur diesen einen
 * Mundpunkt, keine Winkel.
 */
const TEMPLATE = {
  leftEye: { x: 38.29, y: 51.69 },
  rightEye: { x: 73.53, y: 51.5 },
  mouth: { x: 56.14, y: 92.29 },
} as const;

export interface Point {
  x: number;
  y: number;
}

/**
 * Die Punkte, die zum Ausrichten gebraucht werden – in Bildpunkten des
 * Quellbildes, nicht normalisiert.
 */
export interface FaceLandmarks {
  leftEye: Point;
  rightEye: Point;
  mouth: Point;
}

// ── Modell laden ───────────────────────────────────────────────────────────

let sessionPromise: Promise<InferenceSession> | null = null;

async function loadSession(): Promise<InferenceSession> {
  const ort = await import('onnxruntime-web/wasm');
  // Ohne diese Zuweisung sucht die Laufzeit ihre .wasm-Datei neben dem
  // JavaScript-Bündel unter /assets/ – dort liegt sie nicht.
  ort.env.wasm.wasmPaths = { wasm: WASM_FILE };
  // Mehrfädigkeit braucht `SharedArrayBuffer` und damit COOP/COEP-Kopfzeilen,
  // die hier niemand gesetzt hat. Ein Faden ist für ein Gesicht schnell genug.
  ort.env.wasm.numThreads = 1;

  return ort.InferenceSession.create(MODEL_URL, {
    executionProviders: ['wasm'],
    graphOptimizationLevel: 'all',
  });
}

/** `true`, sobald das Modell im Speicher liegt. Nur für die Anzeige. */
export function embedderReady(): boolean {
  return sessionPromise !== null;
}

// ── Ausrichten ─────────────────────────────────────────────────────────────

/**
 * Ähnlichkeitstransformation, die drei Punkte möglichst gut auf drei andere
 * legt – Drehung, gleichmäßiger Maßstab und Verschiebung, aber kein Scheren
 * und kein ungleiches Stauchen.
 *
 * Geschlossene Lösung (2D-Procrustes): beide Punktwolken auf ihren Schwerpunkt
 * zentrieren, dann ergibt sich der gedrehte und skalierte Anteil direkt aus
 * zwei Summen. Eine Singulärwertzerlegung braucht es dafür in der Ebene nicht.
 *
 * Zurück kommen die sechs Zahlen in der Reihenfolge, die `setTransform` des
 * Zeichenkontexts erwartet.
 */
function similarityTransform(
  from: readonly Point[],
  to: readonly Point[],
): { a: number; b: number; c: number; d: number; e: number; f: number } {
  const n = from.length;
  const mean = (points: readonly Point[]) => ({
    x: points.reduce((sum, p) => sum + p.x, 0) / n,
    y: points.reduce((sum, p) => sum + p.y, 0) / n,
  });
  const from0 = mean(from);
  const to0 = mean(to);

  let dot = 0;
  let cross = 0;
  let norm = 0;
  for (let i = 0; i < n; i++) {
    const sx = from[i]!.x - from0.x;
    const sy = from[i]!.y - from0.y;
    const dx = to[i]!.x - to0.x;
    const dy = to[i]!.y - to0.y;
    dot += sx * dx + sy * dy;
    cross += sx * dy - sy * dx;
    norm += sx * sx + sy * sy;
  }

  // Entartete Eingabe (alle Punkte übereinander): unverändert lassen, statt
  // durch Null zu teilen.
  if (norm === 0) return { a: 1, b: 0, c: 0, d: 1, e: to0.x - from0.x, f: to0.y - from0.y };

  const cos = dot / norm;
  const sin = cross / norm;
  return {
    a: cos,
    b: sin,
    c: -sin,
    d: cos,
    e: to0.x - (cos * from0.x - sin * from0.y),
    f: to0.y - (sin * from0.x + cos * from0.y),
  };
}

// ── Vermessen ──────────────────────────────────────────────────────────────

/**
 * Ein ausgerichtetes Gesicht in eine Zahlenreihe übersetzen.
 *
 * `landmarks` sind Augen und Mundmitte in Bildpunkten von `image`. Woher sie
 * kommen, ist der Funktion gleich – aus der Erkennung beim Hochladen oder aus
 * einem Nachlauf über vorhandene Markierungen.
 *
 * Die Zahlenreihe ist auf Länge 1 gebracht. Damit ist der Kosinusabstand
 * zweier Reihen ihr Skalarprodukt, und pgvector kann mit `<=>` direkt darauf
 * suchen.
 */
export async function embedFace(
  image: HTMLImageElement | HTMLCanvasElement,
  landmarks: FaceLandmarks,
): Promise<Float32Array> {
  sessionPromise ??= loadSession();
  let session: InferenceSession;
  try {
    session = await sessionPromise;
  } catch (error) {
    // Ein gescheiterter Ladeversuch darf sich nicht festsetzen.
    sessionPromise = null;
    throw error;
  }

  const canvas = document.createElement('canvas');
  canvas.width = INPUT_PX;
  canvas.height = INPUT_PX;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) throw new Error('Zeichenfläche nicht verfügbar');
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';

  const { a, b, c, d, e, f } = similarityTransform(
    [landmarks.leftEye, landmarks.rightEye, landmarks.mouth],
    [TEMPLATE.leftEye, TEMPLATE.rightEye, TEMPLATE.mouth],
  );

  const ort = await import('onnxruntime-web/wasm');

  /** Einmal ausrichten, zeichnen und durchs Modell schicken. */
  const measure = async (mirrored: boolean): Promise<Float32Array> => {
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.clearRect(0, 0, INPUT_PX, INPUT_PX);
    if (mirrored) {
      // Spiegeln, *bevor* ausgerichtet wird: so entsteht dasselbe normlagige
      // Gesicht, nur seitenverkehrt.
      context.translate(INPUT_PX, 0);
      context.scale(-1, 1);
    }
    context.transform(a, b, c, d, e, f);
    context.drawImage(image, 0, 0);
    context.setTransform(1, 0, 0, 1, 0, 0);

    const { data } = context.getImageData(0, 0, INPUT_PX, INPUT_PX);
    const pixels = INPUT_PX * INPUT_PX;

    // NCHW und BGR: SFace ist mit OpenCV trainiert, und dessen Bilder sind BGR.
    // Die Skalierung steckt im Modell selbst (die ersten beiden Knoten ziehen ab
    // und multiplizieren), hier gehen rohe 0…255-Werte hinein.
    const input = new Float32Array(3 * pixels);
    for (let p = 0; p < pixels; p++) {
      input[p] = data[p * 4 + 2]!;
      input[pixels + p] = data[p * 4 + 1]!;
      input[2 * pixels + p] = data[p * 4]!;
    }

    const feeds: Record<string, Tensor> = {
      [session.inputNames[0]!]: new ort.Tensor('float32', input, [1, 3, INPUT_PX, INPUT_PX]),
    };
    const output = await session.run(feeds);
    return normalize(output[session.outputNames[0]!]!.data as Float32Array);
  };

  const upright = await measure(false);
  const mirrored = await measure(true);

  // Beide Ansichten gemittelt – in der Gesichtserkennung ein üblicher Kniff.
  // Gemessen an 37 benannten Gesichtern dieses Archivs hebt das Spiegeln die
  // Trefferquote von 27 auf 28 von 32 und senkt den stärksten *falschen*
  // Treffer; es kostet einen zweiten Durchlauf durch das Modell, also wenige
  // Millisekunden. Dass es die Zahlen verändert, steht in der Modellkennung –
  // sonst würden alte und neue Reihen vermischt.
  const sum = new Float32Array(upright.length);
  for (let i = 0; i < sum.length; i++) sum[i] = upright[i]! + mirrored[i]!;
  return normalize(sum);
}

/** Auf Länge 1 bringen, damit Kosinusabstand und Skalarprodukt dasselbe sind. */
export function normalize(vector: Float32Array): Float32Array {
  let sum = 0;
  for (const value of vector) sum += value * value;
  const length = Math.sqrt(sum) || 1;
  const out = new Float32Array(vector.length);
  for (let i = 0; i < vector.length; i++) out[i] = vector[i]! / length;
  return out;
}

/**
 * Ähnlichkeit zweier Zahlenreihen: 1 bedeutet gleich, 0 nichts miteinander zu
 * tun, negative Werte gegensätzlich. Setzt voraus, dass beide auf Länge 1
 * gebracht sind.
 */
export function similarity(a: Float32Array, b: Float32Array): number {
  let sum = 0;
  for (let i = 0; i < a.length; i++) sum += a[i]! * b[i]!;
  return sum;
}
